<template>
  <component :is="tourLayout">
    <div v-if="listing?.items" class="w-full">
      <AppBreadcrumb :trail="trail" />

      <header
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 my-4 flex flex-col gap-2"
      >
        <h1 class="text-3xl font-black">{{ heading }}</h1>
        <p v-if="intro" class="max-w-4xl text-gray-300">{{ intro }}</p>
        <nav aria-label="Sort models" class="flex flex-wrap gap-2 mt-2">
          <a
            v-for="option in SORTS"
            :key="`sort-${option.key}`"
            :href="sortPath(option)"
            :aria-current="option.key === sort.key ? 'page' : undefined"
            class="px-3 py-1 rounded border text-sm"
            :class="
              option.key === sort.key
                ? 'border-white text-white'
                : 'border-neutral-600 text-gray-300 hover:text-white'
            "
          >
            {{ option.label }}
          </a>
        </nav>
      </header>

      <SectionContainer id="models">
        <div class="grid gap-2 px-2 sm:px-0" :class="gridClass">
          <div
            v-for="(actor, i) in listing.items"
            :key="`actor-card-${actor.cachedSlug}`"
          >
            <ActorCard :actor="actor" :eager="i < 4" />
          </div>
        </div>
      </SectionContainer>

      <p
        v-if="outro.length"
        class="w-full max-w-7xl mx-auto px-3 xl:px-0 mt-6 text-gray-300"
      >
        <template v-for="(segment, i) in outro" :key="`outro-${i}`">
          <NuxtLink v-if="segment.to" :to="segment.to" class="underline">
            {{ segment.text }}
          </NuxtLink>
          <template v-else>{{ segment.text }}</template>
        </template>
      </p>

      <div class="h-28 flex px-2.5 justify-center items-center gap-2.5">
        <Pagination
          resource="models"
          :current-page="page"
          :total-pages="totalPages"
          :filters="sort.param ? `sort=${sort.param}` : ''"
        />
      </div>
    </div>
  </component>
</template>

<script setup>
  // The site's performers, 24 a page, in one of three orders. Each order and
  // each page is its own canonical (/models?sort=name&page=2), so all of them
  // get indexed; ?page=1 redirects to the address without it, so page 1 has
  // one address. The default order, Top Rated, is the bare /models.
  defineOptions({
    name: "ModelsPage",
  })

  useRedirectInAgeRestrictedArea()

  // `param` is the ?sort= value (none for the default), `api` the order
  // /api/actors knows it by.
  const SORTS = [
    { key: "top_rated", label: "Top Rated", api: "top_rated" },
    { key: "newest", label: "Newest", param: "newest", api: "latest" },
    { key: "name", label: "A to Z", param: "name", api: "name" },
  ]

  const currentSite = inject("currentSite")
  const inAgeRestrictedLocation = inject("inAgeRestrictedLocation")
  const route = useRoute()
  const { notFoundUrl, useVerticalActorCards } = useSiteSettings(
    currentSite.value,
  )
  const tourLayout = useTourLayout(currentSite.value)
  const path = "/models"
  const sort = SORTS.find((s) => s.param === route.query.sort) || SORTS[0]
  const page = Math.max(1, parseInt(route.query.page) || 1)
  const perPage = 24

  const sortPath = (option) =>
    option.param ? `${path}?sort=${option.param}` : path
  const listPath = sortPath(sort)
  const pagePath = (n) =>
    n > 1 ? `${listPath}${sort.param ? "&" : "?"}page=${n}` : listPath

  if (route.query.page === "1") {
    await navigateTo(listPath, { redirectCode: 301 })
  }

  const gridClass = useVerticalActorCards
    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
    : "card-grid"

  const { data: listing } = await useApi("/api/actors", {
    query: {
      sort: sort.api,
      gender: currentSite.value.gender,
      per_page: perPage,
      page,
      vertical: useVerticalActorCards,
      age_restricted: inAgeRestrictedLocation,
    },
  })

  const totalPages = computed(() => listing.value?.pagination?.totalPages || 1)

  if (!listing.value?.items) {
    await navigateTo(notFoundUrl, { external: true, redirectCode: 302 })
  } else if (page > totalPages.value) {
    await navigateTo(listPath, { redirectCode: 302 })
  }

  // The host useSiteSeo picks the page's copy by. Its name, not seoSiteName,
  // is the brand: the H1s spell it "Tiny4K", where seoSiteName says "Tiny 4K".
  const seoSite = computed(() => currentSite.value.alias || currentSite.value)
  const brand = computed(() => seoSite.value.name || currentSite.value.name)

  const heading = computed(
    () => sitePageH1(seoSite.value.host, path) || `${brand.value} Models`,
  )

  // "177 models and counting, …": the site's copy, with its model count.
  const intro = computed(() => {
    const count = listing.value?.pagination?.totalItems ?? 0
    return sitePageIntro(seoSite.value.host, path)?.replaceAll(
      "{count}",
      count.toLocaleString("en-US"),
    )
  })

  // The paragraph under the grid, on the first page of the default order
  // only. Its tag words link to the tag pages the site has.
  const outroCopy =
    page === 1 && !sort.param ? sitePageOutro(seoSite.value.host, path) : null
  const { data: tags } = outroCopy
    ? await useApi("/api/tags")
    : { data: ref([]) }
  const outro = computed(() =>
    outroCopy
      ? linkedCopySegments(
          outroCopy,
          (tags.value || []).map((tag) => tag.slug),
        )
      : [],
  )

  const trail = computed(() => [
    { name: currentSite.value.name, path: "/" },
    { name: "Models" },
  ])

  if (listing.value?.items) {
    const topModel = () => listing.value?.items?.[0]

    useSiteSeo(
      {
        defaultTitle: "{{ siteName }} : Best Pornstars Hot {{ siteGender }}",
        defaultDescription:
          "Find your favorite {{ siteGender }} on {{ siteName }}",
      },
      { image: () => topModel()?.thumbUrl, imageAlt: () => topModel()?.name },
      {
        canonicalPage: page,
        canonicalPath: listPath,
        titleSuffix: sort.param ? sort.label : null,
      },
    )

    const origin = publicSiteOrigin(useRequestURL().href)
    const pageUrl = (n) => `${origin}${pagePath(n)}`

    const schema = computed(() =>
      buildCollectionPageSchema({
        origin,
        url: pageUrl(page),
        name: heading.value,
        description: intro.value,
        trail: trail.value,
        items: listing.value.items.map((actor) => ({
          path: `/models/${actor.cachedSlug}`,
          name: actor.name,
        })),
        startPosition: (page - 1) * perPage + 1,
        itemListOrder: { top_rated: "Descending", name: "Ascending" }[sort.key],
      }),
    )

    useHead({
      link: computed(() =>
        [
          page > 1 && { rel: "prev", href: pageUrl(page - 1) },
          page < totalPages.value && { rel: "next", href: pageUrl(page + 1) },
        ].filter(Boolean),
      ),
      script: computed(() =>
        schema.value
          ? [
              {
                key: "schema-CollectionPage",
                type: "application/ld+json",
                innerHTML: serializeJsonLd(schema.value),
              },
            ]
          : [],
      ),
    })
  }
</script>
